import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { listInsuranceCatalogFn, SELF_PAY_ID } from "@/lib/insurance-catalog.functions";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { ProviderPhoto } from "@/components/ProviderPhoto";
import {
  findInsurance,
  findProvidersByIdentifier,
  rankProvider,
  parseLocationToCityState,
  specialtyToDbPattern,
  SPECIALTIES,
  CITIES,
  INSURANCES,
  cacheLastSearchResults,
  type Provider,
} from "@/data/heallexa";
import { aiParseSearch } from "@/lib/ai-search.functions";
import { buildProviderSlug } from "@/lib/provider-slug";
import { stateFromNameSlug } from "@/lib/seo-slugs";
import type { SearchLoaderResult } from "@/lib/search-loader";
import { resolveCityFromLabel, type ResolvedCity } from "@/lib/providers-nav";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MapPin, Sparkles, Loader2, SlidersHorizontal, Search as SearchIcon, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { ProviderCard } from "@/components/search/ProviderCard";
import { useI18n } from "@/i18n/I18nProvider";
import { BackButton } from "@/components/BackButton";
import { LocationAutocomplete } from "@/components/forms/LocationAutocomplete";
import { SpecialtySelect } from "@/components/forms/SpecialtySelect";
import type { LocationSuggestion } from "@/lib/location-autocomplete";

// Route-agnostic core of the search UI, shared by /search, /providers/$id
// (specialty branch), and /providers/$state/$city — each route wrapper owns
// its own useSearch/useNavigate and decides what a committed filter change
// means for ITS URL (stay + update a query param, or navigate elsewhere);
// this component just reports commits via callbacks. Rating/gender/sort/
// verified/premium/accepting-new stay pure internal state here, matching
// /search's pre-existing behavior — they were never URL-synced even on
// /search itself, so there's nothing to thread through props for them.

type AiResult = {
  specialty: string;
  city: string;
  insurance: string;
  keywords: string[];
  summary: string;
  intent: string;
  lang?: "en" | "es";
};

function scoreProvider(p: Provider, ai: AiResult | null): number {
  let score = rankProvider(p) * 10;
  score += (Number(p.rating) || 0);
  if (!ai) return score;
  const hay = [
    p.name, p.practice, p.specialty, p.city, p.state ?? "",
    p.about ?? "", (p.services || []).join(" "), (p.languages || []).join(" "),
  ].join(" ").toLowerCase();
  if (ai.specialty && p.specialty.toLowerCase().includes(ai.specialty.toLowerCase())) score += 50;
  if (ai.city && p.city.toLowerCase().includes(ai.city.split(",")[0].toLowerCase())) score += 40;
  if (ai.insurance) {
    const ins = INSURANCES.find(
      (i) => i.short.toLowerCase() === ai.insurance.toLowerCase() || i.name.toLowerCase() === ai.insurance.toLowerCase()
    );
    if (ins && (p.insurance || []).includes(ins.id)) score += 30;
  }
  for (const kw of ai.keywords) {
    if (kw && hay.includes(kw.toLowerCase())) score += 8;
  }
  return score;
}

function matchedKeywords(p: Provider, ai: AiResult | null): string[] {
  if (!ai) return [];
  const hay = [
    p.name, p.practice, p.specialty, p.city,
    p.about ?? "", (p.services || []).join(" "), (p.languages || []).join(" "),
  ].join(" ").toLowerCase();
  return ai.keywords.filter((k) => k && hay.includes(k.toLowerCase()));
}

function specialtyMatches(providerSpecialty: string | null | undefined, selectedSpecialty: string): boolean {
  const provider = (providerSpecialty || "").toLowerCase();
  const selected = selectedSpecialty.trim();
  if (!selected || selected === "any") return true;
  const pattern = specialtyToDbPattern(selected);
  if (pattern) {
    try {
      return new RegExp(pattern, "i").test(provider);
    } catch { /* fall through to normalized text comparison */ }
  }
  const normalizedSelected = selected.toLowerCase().replace(/s\b/, "");
  return provider.includes(selected.toLowerCase()) || provider.includes(normalizedSelected);
}

export type { ResolvedCity };

export type SearchExperienceProps = {
  loaderData: SearchLoaderResult;
  initial: { q: string; location: string; radius: number };
  isRouteLoading: boolean;
  onQueryChange: (q: string) => void;
  onLocationSubmit: (location: string) => void;
  onSpecialtyCommit: (specialty: string, currentCity: string) => void;
  onCityCommit: (cityLabel: string, matched: ResolvedCity | null, currentSpecialty: string) => void;
  onRadiusChange: (radius: number) => void;
  onPageChange: (page: number) => void;
  /** Extra content (SEO copy, FAQ, etc.) rendered after the results, before the footer. */
  belowResults?: React.ReactNode;
};

const PAGE_SIZE = 20;

export function SearchExperience({
  loaderData,
  initial,
  isRouteLoading,
  onQueryChange,
  onLocationSubmit,
  onSpecialtyCommit,
  onCityCommit,
  onRadiusChange,
  onPageChange,
  belowResults,
}: SearchExperienceProps) {
  const { results, filters, aiQuery, ai: loaderAi, meta, page: loaderPage, hasMore, total, totalCapped } = loaderData;
  const { t } = useI18n();
  const [ai, setAi] = useState<AiResult | null>(loaderAi as AiResult | null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Debounced search input that drives the `q` URL param.
  const [queryDraft, setQueryDraft] = useState<string>(initial.q);
  const lastSubmittedRef = useRef<string>(initial.q);
  useEffect(() => {
    setQueryDraft(initial.q);
    lastSubmittedRef.current = initial.q;
  }, [initial.q]);
  useEffect(() => {
    const next = queryDraft.trim();
    if (next === (lastSubmittedRef.current ?? "")) return;
    const handle = setTimeout(() => {
      lastSubmittedRef.current = next;
      onQueryChange(next);
    }, 400);
    return () => clearTimeout(handle);
  }, [queryDraft, onQueryChange]);

  // Top search-bar location field — submits on Enter/click (not debounced,
  // since a location change triggers a full server round-trip via the loader).
  const [locationDraft, setLocationDraft] = useState<string>(initial.location);
  useEffect(() => {
    setLocationDraft(initial.location);
  }, [initial.location]);
  const submitLocationSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onLocationSubmit(locationDraft.trim());
  };

  // Client-side refine filters — seed from the loader's echoed filters so the
  // panel reflects deep links (path segment or query param, whichever route
  // this is).
  const initialInsuranceId = useMemo(() => {
    const v = (filters.insurance || "").trim().toLowerCase();
    if (!v) return "any";
    const match = INSURANCES.find(
      (i) => i.id.toLowerCase() === v || i.short.toLowerCase() === v || i.name.toLowerCase() === v,
    );
    return match?.id ?? "any";
  }, [filters.insurance]);
  const [fSpecialty, setFSpecialty] = useState<string>(filters.specialty || "any");
  const [fCity, setFCity] = useState<string>(filters.location || "any");
  const [fInsurance, setFInsurance] = useState<string>(initialInsuranceId);
  // Carrier + plan share one filter value: a plan token embeds its carrier
  // ("aetna:choice-pos-ii"), so the carrier dropdown shows the prefix and the
  // plan dropdown the whole token. No second URL param, no server change.
  const insuranceCarrier = fInsurance.includes(":") ? fInsurance.split(":")[0] : fInsurance;
  const { data: insuranceCatalog } = useQuery({
    queryKey: ["insurance-catalog"],
    queryFn: () => listInsuranceCatalogFn(),
    staleTime: 5 * 60_000,
  });
  // Falls back to the static list until the catalog tables are seeded, so the
  // carrier filter never renders empty.
  const insuranceCarriers = insuranceCatalog?.carriers?.length
    ? insuranceCatalog.carriers
    : INSURANCES.filter((i) => i.id !== SELF_PAY_ID);
  const carrierPlans = useMemo(
    () =>
      insuranceCarrier === "any"
        ? []
        : (insuranceCatalog?.plans ?? []).filter((p) => p.carrier_id === insuranceCarrier),
    [insuranceCatalog, insuranceCarrier],
  );

  const [fMinRating, setFMinRating] = useState<string>("any");
  const [fVerified, setFVerified] = useState(false);
  const [fPremium, setFPremium] = useState(false);
  const [fAcceptingNew, setFAcceptingNew] = useState(false);
  const [fGender, setFGender] = useState<string>("any");
  const [fSort, setFSort] = useState<string>("relevance");
  // When the visitor's location is known, results carry distances → default to
  // sorting by distance. Stops once they pick a sort themselves.
  const sortTouched = useRef(false);
  useEffect(() => {
    if (sortTouched.current) return;
    setFSort(results.some((p) => (p as any).distance_m != null) ? "distance" : "relevance");
  }, [results]);

  // Keep panel in sync when the loader re-runs with different filters (e.g.
  // browser back/forward).
  useEffect(() => {
    setFSpecialty(filters.specialty || "any");
    setFCity(filters.location || "any");
    setFInsurance(initialInsuranceId);
  }, [filters.specialty, filters.location, initialInsuranceId]);

  // The city filter chip used to only narrow the already-loaded page of
  // results client-side (so picking a city could show as few as 1 provider —
  // whatever happened to already be in that batch). Debounce it into a real
  // commit instead, same as queryDraft does for `q`. When the typed text
  // unambiguously matches one of Heallexa's own known cities, resolve it to
  // {city, state} so the caller can jump to a clean URL — this doesn't depend
  // on the counts-autocomplete dropdown (which needs a DB migration that
  // hasn't been applied yet) since it's resolved against the same static
  // city list the /providers pages use.
  const lastSubmittedCityRef = useRef<string>(filters.location || "");
  useEffect(() => {
    const next = fCity === "any" ? "" : fCity.trim();
    if (next === (lastSubmittedCityRef.current ?? "")) return;
    const handle = setTimeout(() => {
      lastSubmittedCityRef.current = next;
      onCityCommit(next, next ? resolveCityFromLabel(next) : null, fSpecialty);
    }, 400);
    return () => clearTimeout(handle);
  }, [fCity, fSpecialty, onCityCommit]);

  const resetFilters = () => {
    setFSpecialty("any"); setFCity("any"); setFInsurance("any");
    setFMinRating("any"); setFVerified(false); setFPremium(false);
    setFAcceptingNew(false); setFGender("any"); setFSort("relevance");
  };

  const anyFilterActive =
    fSpecialty !== "any" || fCity !== "any" || fInsurance !== "any" ||
    fMinRating !== "any" || fVerified || fPremium || fAcceptingNew || fGender !== "any";

  // Background AI parse: results already rendered from the loader's
  // fast local-parse path. The LLM call refines filter chips and
  // ranking without blocking the initial render.
  useEffect(() => {
    setAi((loaderAi as AiResult | null) ?? null);
    setAiError(null);
    if (!aiQuery) { setAiLoading(false); return; }
    if (loaderAi) { setAiLoading(false); return; }
    let cancelled = false;
    setAiLoading(true);
    aiParseSearch({
      data: {
        query: aiQuery,
        specialties: SPECIALTIES,
        cities: CITIES,
        insurances: INSURANCES.map((i) => i.short),
      },
    })
      .then((resp) => { if (!cancelled) setAi((resp.result as AiResult | null) ?? null); })
      .catch((e) => { if (!cancelled) setAiError(e?.message ?? "AI unavailable"); })
      .finally(() => { if (!cancelled) setAiLoading(false); });
    return () => { cancelled = true; };
  }, [loaderAi, aiQuery]);

  const ranked = useMemo(() => {
    let base: Provider[];
    if (!aiQuery) base = results;
    else {
      const withScores = results.map((p: Provider) => ({ p, s: scoreProvider(p, ai) }));
      withScores.sort((a: { p: Provider; s: number }, b: { p: Provider; s: number }) => b.s - a.s);
      if (ai && (ai.specialty || ai.city || ai.insurance || ai.keywords.length)) {
        const filtered = withScores.filter(({ p }: { p: Provider; s: number }) => {
          const specOk = !ai.specialty || p.specialty.toLowerCase().includes(ai.specialty.toLowerCase());
          const aiParsed = parseLocationToCityState(ai.city);
          const cityOk = !aiParsed.city || p.city.toLowerCase().includes(aiParsed.city.toLowerCase());
          const stateOk = !aiParsed.state || (p.state || "").toUpperCase() === aiParsed.state;
          return specOk && cityOk && stateOk;
        });
        const list = filtered.length > 0 ? filtered : withScores.slice(0, 12);
        base = list.map((x: { p: Provider; s: number }) => x.p);
      } else {
        base = withScores.map((x: { p: Provider; s: number }) => x.p);
      }
    }
    return base.filter((p) => {
      if (!specialtyMatches(p.specialty, fSpecialty)) return false;
      if (fCity !== "any") {
        const parsed = parseLocationToCityState(fCity);
        if (parsed.city && !p.city.toLowerCase().includes(parsed.city.toLowerCase())) return false;
        if (parsed.state && (p.state || "").toUpperCase() !== parsed.state) return false;
      }
      if (fInsurance !== "any" && !(p.insurance || []).includes(fInsurance)) return false;
      if (fMinRating !== "any" && (Number(p.rating) || 0) < Number(fMinRating)) return false;
      if (fVerified && p.listing_status === "unclaimed") return false;
      if (fPremium && p.listing_status !== "premium") return false;
      if (fAcceptingNew) {
        const a = ((p as any).availability || "").toLowerCase();
        const hasDays = ((p as any).available_days || []).length > 0;
        const accepting = a
          ? !/(not accepting|closed to new|waitlist|full)/.test(a)
          : hasDays;
        if (!accepting) return false;
      }
      if (fGender !== "any") {
        const g = String((p as any).gender || "").toLowerCase();
        if (!g.startsWith(fGender)) return false;
      }
      return true;
    })
      .slice()
      .sort((a, b) => {
        if (fSort === "rating") return (Number(b.rating) || 0) - (Number(a.rating) || 0);
        if (fSort === "reviews") return (Number((b as any).reviews_count) || 0) - (Number((a as any).reviews_count) || 0);
        if (fSort === "distance") {
          return (Number((a as any).distance_m) || Infinity) - (Number((b as any).distance_m) || Infinity);
        }
        return 0;
      });
  }, [results, ai, aiQuery, fSpecialty, fCity, fInsurance, fMinRating, fVerified, fPremium, fAcceptingNew, fGender, fSort]);

  useEffect(() => {
    cacheLastSearchResults(ranked);
  }, [ranked]);

  const resultsTitle = initial.q
    ? `"${initial.q}"`
    : filters.specialty && filters.location
    ? `${filters.specialty} in ${filters.location}`
    : filters.specialty
    ? filters.specialty
    : filters.location
    ? `Providers in ${filters.location}`
    : "All providers";

  const countLabel = ranked.length === 0
    ? "No providers found"
    : (() => {
      const startIdx = (loaderPage - 1) * PAGE_SIZE + 1;
      const endIdx = (loaderPage - 1) * PAGE_SIZE + ranked.length;
      const totalLabel = total > 0
        ? `${total.toLocaleString()}${totalCapped ? "+" : ""}`
        : `${endIdx}${hasMore ? "+" : ""}`;
      return `${startIdx}–${endIdx} of ${totalLabel} providers found`;
    })();

  return (
    <div className="min-h-screen bg-background pb-1">
      <Navbar />
      <header className="pt-32 md:pt-20 pb-3 bg-background">
        <div className="mx-auto max-w-[1200px] px-6">
          <BackButton className="mb-2 -ml-2" />

          <form onSubmit={submitLocationSearch} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
            <div className="flex items-center gap-3 min-w-0 h-11 px-4 border border-border bg-background rounded-md focus-within:ring-2 focus-within:ring-primary/30">
              <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <input
                type="text"
                value={locationDraft}
                onChange={(e) => setLocationDraft(e.target.value)}
                placeholder="City, ZIP, or state"
                aria-label="Location"
                className="w-full bg-transparent text-[14px] text-foreground placeholder:text-muted-foreground outline-none"
              />
            </div>
            <label className="relative flex items-center gap-3 min-w-0 h-11 px-4 border border-border bg-background rounded-md focus-within:ring-2 focus-within:ring-primary/30">
              <SearchIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <input
                type="search"
                value={queryDraft}
                onChange={(e) => setQueryDraft(e.target.value)}
                placeholder="Provider name, specialty, or ask a question"
                aria-label="Refine search"
                className="w-full bg-transparent text-[14px] text-foreground placeholder:text-muted-foreground outline-none"
              />
              {isRouteLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" aria-hidden />
              )}
            </label>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 h-11 px-8 text-[14px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-md"
            >
              Search <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          {initial.q && (
            <div className="mt-4 max-w-2xl">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-primary" /> AI search
              </p>
              {aiLoading && (
                <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Understanding your request…
                </p>
              )}
              {ai && (
                <div className="space-y-2">
                  {ai.summary && (
                    <p className="text-sm text-foreground/90">{ai.summary}</p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {ai.specialty && <FilterPill label={ai.specialty} />}
                    {ai.city && <FilterPill label={ai.city} />}
                    {ai.insurance && <FilterPill label={ai.insurance} />}
                    {ai.intent && <FilterPill label={ai.intent} muted />}
                  </div>
                </div>
              )}
              {aiError && (
                <p className="text-sm text-destructive">AI: {aiError} (showing best matches by keyword)</p>
              )}
            </div>
          )}

          <DirectProviderLookup />
        </div>
      </header>

      {/* FILTER BAR — compact, horizontal, wraps on small screens */}
      <div className="border-b border-border">
        <div className="mx-auto max-w-[1200px] px-6 py-2.5 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-2 text-[13px] text-muted-foreground mr-1 shrink-0">
            <SlidersHorizontal className="h-4 w-4" /> {t.filters.title}
          </span>

          <div className={`w-auto min-w-[140px] max-w-[200px] px-3 flex items-center shrink-0 ${filterChipClass(fSpecialty !== "any")}`}>
            <SpecialtySelect
              value={fSpecialty === "any" ? "" : fSpecialty}
              onChange={(v) => {
                const next = v.trim() ? v : "any";
                setFSpecialty(next);
                if (next !== "any") onSpecialtyCommit(next, fCity);
              }}
              placeholder={t.filters.specialty}
              allowCustom={false}
            />
          </div>

          <div
            className={`w-[150px] px-3 flex items-center gap-1.5 shrink-0 focus-within:border-primary/50 focus-within:bg-primary/5 ${filterChipClass(fCity !== "any")}`}
          >
            <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden />
            <LocationAutocomplete
              value={fCity === "any" ? "" : fCity}
              onChange={(value) => setFCity(value.trim() ? value : "any")}
              onSelect={(s: LocationSuggestion) => {
                if (!s.city || !s.state) return;
                const state = stateFromNameSlug(s.state);
                onCityCommit(
                  [s.city, s.state].join(", "),
                  state ? { city: s.city, state: state.code } : null,
                  fSpecialty,
                );
              }}
              placeholder={t.filters.city}
              className="flex-1 min-w-0"
              inputClassName="h-9 w-full bg-transparent border-0 shadow-none px-0 text-[13px] outline-none focus-visible:ring-0"
              withCounts
            />
          </div>

          <RadiusFilter radius={initial.radius} hasLocation={Boolean(parseLocationToCityState(fCity === "any" ? "" : fCity).city)} onRadiusChange={onRadiusChange} />

          {/* Carrier, then an optional plan refinement. Both write the SAME
              `fInsurance` param — a plan token ("aetna:choice-pos-ii") already
              contains its carrier, so the server filter is unchanged and the
              URL keeps one insurance value. Picking a carrier alone still
              matches every doctor who listed only specific plans, because the
              doctor picker always writes the carrier token too. */}
          <Select value={insuranceCarrier} onValueChange={(v) => setFInsurance(v)}>
            <SelectTrigger className={`w-auto min-w-[110px] shrink-0 ${filterChipClass(fInsurance !== "any")}`}>
              <SelectValue placeholder={t.filters.insurance} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="any">{t.filters.any} {t.filters.insurance}</SelectItem>
              {insuranceCarriers.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
            </SelectContent>
          </Select>

          {carrierPlans.length > 0 && (
            <Select
              value={fInsurance.includes(":") ? fInsurance : "any"}
              onValueChange={(v) => setFInsurance(v === "any" ? insuranceCarrier : v)}
            >
              <SelectTrigger className={`w-auto min-w-[130px] shrink-0 ${filterChipClass(fInsurance.includes(":"))}`}>
                <SelectValue placeholder="Plan" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="any">All plans</SelectItem>
                {carrierPlans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}{p.plan_type ? ` (${p.plan_type})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={fMinRating} onValueChange={setFMinRating}>
            <SelectTrigger className={`w-auto min-w-[100px] shrink-0 ${filterChipClass(fMinRating !== "any")}`}>
              <SelectValue placeholder={t.filters.minRating} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t.filters.minRating}</SelectItem>
              <SelectItem value="3">3.0+ rating</SelectItem>
              <SelectItem value="4">4.0+ rating</SelectItem>
              <SelectItem value="4.5">4.5+ rating</SelectItem>
            </SelectContent>
          </Select>

          <Select value={fGender} onValueChange={setFGender}>
            <SelectTrigger className={`w-auto min-w-[100px] shrink-0 ${filterChipClass(fGender !== "any")}`}>
              <SelectValue placeholder="Gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any gender</SelectItem>
              <SelectItem value="f">Female</SelectItem>
              <SelectItem value="m">Male</SelectItem>
            </SelectContent>
          </Select>

          <ToggleChip active={fVerified} onClick={() => setFVerified((v) => !v)}>{t.filters.verifiedOnly}</ToggleChip>
          <ToggleChip active={fPremium} onClick={() => setFPremium((v) => !v)}>{t.filters.premiumOnly}</ToggleChip>
          <ToggleChip active={fAcceptingNew} onClick={() => setFAcceptingNew((v) => !v)}>Accepting new</ToggleChip>

          {anyFilterActive && (
            <button
              type="button"
              onClick={resetFilters}
              className="h-9 px-3 text-[13px] text-muted-foreground hover:text-foreground rounded-md transition-colors shrink-0"
            >
              {t.filters.reset}
            </button>
          )}

          <label className="ml-auto flex items-center gap-2 text-[13px] text-muted-foreground shrink-0">
            Sort
            <Select value={fSort} onValueChange={(v) => { sortTouched.current = true; setFSort(v); }}>
              <SelectTrigger className={`w-auto min-w-[130px] ${filterChipClass(false)}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="relevance">Relevance</SelectItem>
                {results.some((p) => (p as any).distance_m != null) && (
                  <SelectItem value="distance">Nearest</SelectItem>
                )}
                <SelectItem value="rating">Highest rating</SelectItem>
                <SelectItem value="reviews">Most reviews</SelectItem>
              </SelectContent>
            </Select>
          </label>
        </div>
      </div>

      <main className="mx-auto max-w-[1200px] px-6 py-6">
        <div className="mb-5">
          <p className="text-[12px] uppercase tracking-[0.14em] text-muted-foreground">
            {isRouteLoading ? "Searching…" : countLabel}
          </p>
          <h1 className="font-display text-[28px] font-semibold tracking-tight text-foreground mt-2">
            {resultsTitle}
          </h1>
          {meta && meta.usedRadius && (
            <p className="mt-2 text-xs text-primary inline-flex items-center gap-1.5">
              <MapPin className="h-3 w-3" />
              Within {meta.requestedRadiusMiles} mi of {meta.city}{meta.state ? `, ${meta.state}` : ""} — including nearby cities
            </p>
          )}
          {meta && !meta.usedRadius && meta.requestedRadiusMiles > 0 && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-500">
              Couldn't pinpoint that city on a map — showing exact-city results only.
            </p>
          )}
        </div>

        {ranked.length === 0 ? (
          <div className="card-surface rounded-md p-12 text-center">
            <p className="text-muted-foreground">No providers matched your search. Try broader filters or browse by specialty.</p>
            <Button asChild className="mt-6"><Link to="/browse">Browse all</Link></Button>
          </div>
        ) : (
          <ResultsList ranked={ranked} ai={ai} page={loaderPage} hasMore={hasMore} total={total} onPageChange={onPageChange} />
        )}
      </main>

      {belowResults}

      <FooterSection />
    </div>
  );
}

function ResultsList({
  ranked,
  ai,
  page,
  hasMore,
  total,
  onPageChange,
}: {
  ranked: Provider[];
  ai: AiResult | null;
  page: number;
  hasMore: boolean;
  total: number;
  onPageChange: (page: number) => void;
}) {
  // Derive total pages from the count when available, otherwise grow with the
  // current page + hasMore signal so users can still navigate forward.
  const knownPages = total > 0 ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 0;
  const fallbackPages = page + (hasMore ? 1 : 0);
  const totalPages = Math.max(knownPages, fallbackPages, 1);

  return (
    <div className="grid gap-5">
      {ranked.map((p) => (
        <ProviderCard key={p.id} p={p} highlights={matchedKeywords(p, ai)} />
      ))}

      {totalPages > 1 && (
        <nav aria-label="Pagination" className="flex items-center justify-center gap-3 pt-6">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 text-[13px] h-9 px-3 rounded-md border border-border bg-background disabled:opacity-40 hover:bg-muted transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <span className="text-[13px] text-muted-foreground tabular-nums">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={!hasMore && page >= totalPages}
            className="inline-flex items-center gap-1 text-[13px] h-9 px-3 rounded-md border border-border bg-background disabled:opacity-40 hover:bg-muted transition-colors"
            aria-label="Next page"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </nav>
      )}
    </div>
  );
}

const RADIUS_OPTIONS = [0, 5, 10, 15, 25, 50];

function RadiusFilter({
  radius,
  hasLocation,
  onRadiusChange,
}: {
  radius: number;
  hasLocation: boolean;
  onRadiusChange: (radius: number) => void;
}) {
  const current = Math.min(Math.max(Number(radius) || 0, 0), 50);
  return (
    <Select
      value={String(current)}
      onValueChange={(v) => onRadiusChange(Number(v))}
      disabled={!hasLocation}
    >
      <SelectTrigger className={`w-auto min-w-[110px] shrink-0 ${filterChipClass(current > 0)}`} title={!hasLocation ? "Pick a city to enable radius" : undefined}>
        <SelectValue placeholder="Radius" />
      </SelectTrigger>
      <SelectContent>
        {RADIUS_OPTIONS.map((mi) => (
          <SelectItem key={mi} value={String(mi)}>{mi === 0 ? "Exact city" : `Within ${mi} mi`}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Shared pill styling for every filter control (Select triggers, chip wrappers)
// so they read as one consistent row instead of mixed default-shadcn + custom.
function filterChipClass(active: boolean) {
  return `h-9 rounded-md border text-[13px] shadow-none transition-colors ${
    active
      ? "border-primary/50 bg-primary/5 text-foreground"
      : "border-border bg-background text-foreground hover:bg-muted"
  }`;
}

function ToggleChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        active
          ? "inline-flex items-center h-9 px-3 text-[13px] rounded-md border border-primary/50 bg-primary/5 text-foreground font-medium shrink-0 transition-colors"
          : "inline-flex items-center h-9 px-3 text-[13px] rounded-md border border-border bg-background text-foreground hover:bg-muted shrink-0 transition-colors"
      }
    >
      {children}
    </button>
  );
}

function FilterPill({ label, muted }: { label: string; muted?: boolean }) {
  return (
    <span
      className={
        muted
          ? "text-[11px] px-2 py-0.5 rounded-md border border-border text-muted-foreground"
          : "text-[11px] px-2 py-0.5 rounded-md border border-primary/40 text-primary font-medium bg-primary/5"
      }
    >
      {label}
    </span>
  );
}

function DirectProviderLookup() {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [results, setResults] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const q = value.trim();
    if (q.length < 2) { setResults([]); setError(null); return; }
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(() => {
      findProvidersByIdentifier(q, 8)
        .then((rows) => { if (!cancelled) { setResults(rows); setError(null); } })
        .catch((e) => { if (!cancelled) setError(e.message ?? "Lookup failed"); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [value, open]);

  return (
    <div className="mt-3 max-w-xl">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs text-primary hover:underline inline-flex items-center gap-1.5"
        >
          <SearchIcon className="h-3 w-3" /> Looking for a specific provider? Find by name, NPI, license # or practice
        </button>
      ) : (
        <div className="card-surface-flat rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Find a specific provider
            </Label>
            <button
              type="button"
              onClick={() => { setOpen(false); setValue(""); setResults([]); }}
              className="text-[11px] text-muted-foreground hover:text-foreground"
            >
              Close
            </button>
          </div>
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Name, practice, NPI, or license #"
              className="pl-9 h-9"
              aria-label="Find provider by name, NPI, license number or practice"
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
            )}
          </div>
          {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
          {value.trim().length >= 2 && !loading && results.length === 0 && !error && (
            <p className="mt-2 text-xs text-muted-foreground">No providers matched.</p>
          )}
          {results.length > 0 && (
            <ul className="mt-2 divide-y divide-border rounded-md border border-border bg-background overflow-hidden">
              {results.map((p) => (
                <li key={p.id}>
                  <Link
                    to="/providers/$id"
                    params={{ id: buildProviderSlug(p) }}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50"
                  >
                    <ProviderPhoto provider={p} className="h-9 w-9 border border-border shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-foreground truncate">{p.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {p.practice} · {p.specialty} · {p.city}{p.state ? `, ${p.state}` : ""}
                      </div>
                    </div>
                    {((p as any).npi || (p as any).license_number) && (
                      <div className="text-[10px] font-mono text-muted-foreground text-right shrink-0">
                        {(p as any).npi && <div>NPI {(p as any).npi}</div>}
                        {(p as any).license_number && <div>Lic {(p as any).license_number}</div>}
                      </div>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
